/**
 * "Cost of standing still" calculator — pure, browser-only logic.
 *
 * NOTHING here touches the network: the trust promise on the page is that the
 * figures never leave the browser, so this stays a plain synchronous function.
 *
 * The math (see the spec) computes three buckets and a headline total:
 *   A. Run-the-bank tax   = budget × run-the-bank%
 *   B. Tech-debt surcharge = new-project spend × surcharge%
 *   C. Delivery drag      = delayed releases × avg revenue/release × capture%
 *   Total misallocation   = A + B + C
 * plus a "% of budget locked" read derived from the INTERNAL lock-up (A + B
 * only — delivery drag is a revenue-side number, not part of the IT budget).
 */

export interface CalculatorInputs {
  /** box 1 — Annual IT budget, USD. */
  annualItBudget: number;
  /** box 2 — % spent on run-the-bank, 0–100. */
  runTheBankPct: number;
  /** box 3 — Annual spend on new projects, USD. */
  newProjectSpend: number;
  /** box 4 — Releases delayed 6+ months by legacy, whole number. */
  delayedReleases: number;
  /** box 5 — Tech-debt surcharge on new work, 0–100. */
  techDebtSurchargePct: number;
  /** box 6 — Avg. revenue contribution per release, USD. */
  avgRevenuePerRelease: number;
  /** box 7 — Capture probability on delayed releases, 0–100. */
  captureProbabilityPct: number;
}

export interface CalculatorResult {
  runTheBankTax: number;
  techDebtSurcharge: number;
  deliveryDrag: number;
  totalMisallocation: number;
  /** A + B — the part that comes out of the IT budget itself. */
  internalLockup: number;
  /** round( internalLockup / budget × 100 ). 0 when budget is 0/blank. */
  pctBudgetLocked: number;
  /** round( (budget − internalLockup) / budget × 100 ), never below 0. */
  pctAvailableForNewValue: number;
}

/** Sensible defaults so the calculator demonstrates itself on first paint. */
export const DEFAULT_INPUTS: CalculatorInputs = {
  annualItBudget: 120_000_000,
  runTheBankPct: 67,
  newProjectSpend: 40_000_000,
  delayedReleases: 6,
  techDebtSurchargePct: 15,
  avgRevenuePerRelease: 1_500_000,
  captureProbabilityPct: 50,
};

export type InputKind = 'dollar' | 'percent' | 'integer';

export interface InputConfig {
  key: keyof CalculatorInputs;
  label: string;
  helper: string;
  kind: InputKind;
  /** Lives inside the collapsed "Adjust the benchmark assumptions" panel. */
  advanced: boolean;
}

/** Labels, helper copy and defaults straight from the spec. */
export const INPUTS: InputConfig[] = [
  {
    key: 'annualItBudget',
    label: 'Annual IT budget',
    helper: 'Total technology budget for the year, in USD.',
    kind: 'dollar',
    advanced: false,
  },
  {
    key: 'runTheBankPct',
    label: '% spent on run-the-bank',
    helper:
      'Keeping existing systems alive (maintenance, support, infrastructure). Industry benchmark ≈ 67%.',
    kind: 'percent',
    advanced: false,
  },
  {
    key: 'newProjectSpend',
    label: 'Annual spend on new projects',
    helper:
      "Budget for new product, capability, and AI work this year. If unsure, use 'grow + innovate' ≈ 33% of total.",
    kind: 'dollar',
    advanced: false,
  },
  {
    key: 'delayedReleases',
    label: 'Releases delayed 6+ months by legacy',
    helper:
      'Roughly how many planned releases slipped half a year or more because of legacy constraints last year.',
    kind: 'integer',
    advanced: false,
  },
  {
    key: 'techDebtSurchargePct',
    label: 'Tech-debt surcharge on new work',
    helper:
      'McKinsey: 10–20% of every new project is consumed working around tech debt. Default 15%.',
    kind: 'percent',
    advanced: true,
  },
  {
    key: 'avgRevenuePerRelease',
    label: 'Avg. revenue contribution per release',
    helper:
      'Annualized revenue a typical new release contributes. Used to estimate delivery drag.',
    kind: 'dollar',
    advanced: true,
  },
  {
    key: 'captureProbabilityPct',
    label: 'Capture probability on delayed releases',
    helper:
      'Share of delayed-release revenue a competitor captures first. Default 50%.',
    kind: 'percent',
    advanced: true,
  },
];

/** Blank / NaN / Infinity → 0 so the math never breaks. */
const safe = (v: number): number => (Number.isFinite(v) ? v : 0);
/** Non-negative amount. */
const amount = (v: number): number => Math.max(0, safe(v));
/** Percent clamped to 0–100. */
const pct = (v: number): number => Math.min(100, Math.max(0, safe(v)));

export function calculateStandingStill(
  input: CalculatorInputs,
): CalculatorResult {
  const budget = amount(input.annualItBudget);
  const runTheBankTax = budget * (pct(input.runTheBankPct) / 100);
  const techDebtSurcharge =
    amount(input.newProjectSpend) * (pct(input.techDebtSurchargePct) / 100);
  const deliveryDrag =
    amount(input.delayedReleases) *
    amount(input.avgRevenuePerRelease) *
    (pct(input.captureProbabilityPct) / 100);

  const totalMisallocation = runTheBankTax + techDebtSurcharge + deliveryDrag;
  const internalLockup = runTheBankTax + techDebtSurcharge;

  const pctBudgetLocked =
    budget > 0 ? Math.round((internalLockup / budget) * 100) : 0;
  const pctAvailableForNewValue =
    budget > 0
      ? Math.max(0, Math.round(((budget - internalLockup) / budget) * 100))
      : 0;

  return {
    runTheBankTax,
    techDebtSurcharge,
    deliveryDrag,
    totalMisallocation,
    internalLockup,
    pctBudgetLocked,
    pctAvailableForNewValue,
  };
}

/** Abbreviated currency for big numbers, e.g. $90.9M, $1.2B, $857.6B. */
export function formatAbbrev(value: number): string {
  const v = safe(value);
  const abs = Math.abs(v);
  if (abs >= 1e12) {
    return `$${(v / 1e12).toFixed(1)}T`;
  }
  if (abs >= 1e9) {
    return `$${(v / 1e9).toFixed(1)}B`;
  }
  if (abs >= 1e6) {
    return `$${(v / 1e6).toFixed(1)}M`;
  }
  if (abs >= 1e3) {
    return `$${(v / 1e3).toFixed(1)}K`;
  }
  return `$${Math.round(v).toLocaleString('en-US')}`;
}

/** Exact dollars with separators, e.g. $90,900,000. */
export function formatExact(value: number): string {
  return `$${Math.round(safe(value)).toLocaleString('en-US')}`;
}
